import { useEffect, useState } from "react";
import { Sparkles, ChevronDown, ChevronRight, Info } from "lucide-react";
import { getBoideToken, getDiviners, updateDefaultDiviner, type Diviner } from "@/lib/boide-api";
import { refreshBoideUser } from "@/hooks/use-boide-user";
import { toast } from "sonner";
import { DivineRosterDialog } from "./divine-roster-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function DivinerPicker({
  value,
  onChange,
  disabled,
  onDivinerNameChange,
  compact = false,
  ritualCard = false,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onDivinerNameChange?: (name: string) => void;
  compact?: boolean;
  ritualCard?: boolean;
}) {
  const [diviners, setDiviners] = useState<Diviner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDiviners()
      .then((items) => {
        if (!cancelled) {
          setDiviners(items);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Lỗi tải danh sách thầy");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selected = diviners.find((d) => d.id === value);
  const displayName = selected?.ten || (loading ? "..." : value || "Chọn thầy");

  const handleSelect = (id: string) => {
    onChange(id);
    if (!getBoideToken()) return;
    void updateDefaultDiviner(id)
      .then(() => refreshBoideUser())
      .catch(() => toast.error("Chưa lưu được thầy mặc định"));
  };

  useEffect(() => {
    onDivinerNameChange?.(displayName);
  }, [displayName, onDivinerNameChange]);

  return (
    <>
      {ritualCard ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen(true)}
          className="iching-diviner-card flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition hover:border-primary/40 disabled:opacity-60"
          aria-label={`Minh sư đồng hành: ${displayName}. Chạm để đổi thầy.`}
        >
          {selected?.avatar ? (
            <img
              src={selected.avatar}
              alt=""
              className="size-12 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
              {displayName.slice(0, 1)}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-bold tracking-[0.08em] text-primary">
              MINH SƯ ĐỒNG HÀNH
            </span>
            <span className="block text-[15px] font-bold text-foreground">{displayName}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {selected?.mo_ta || selected?.phong_cach || "Thầy bói AI chiêm nghiệm số mệnh"}
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
        </button>
      ) : (
        <div
          className={
            compact
              ? "flex w-full items-center rounded-2xl border border-border/55 bg-background/45 p-1 transition hover:border-primary/50"
              : "inline-flex self-center items-center rounded-full border border-gold-soft/40 bg-background/40 p-1 pl-2 shadow-gold backdrop-blur-md transition-all hover:border-gold-soft/70"
          }
        >
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
            className={
              compact
                ? "flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-2 text-left transition-opacity hover:opacity-80 disabled:opacity-60"
                : "inline-flex items-center gap-2 px-3 py-1.5 transition-opacity hover:opacity-80 disabled:opacity-60"
            }
          >
            <span
              className={
                compact
                  ? "flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                  : "contents"
              }
            >
              <Sparkles className="h-4 w-4 text-primary" />
            </span>
            {compact ? null : (
              <span className="mt-0.5 font-display text-[10px] tracking-[0.2em] text-primary/80">
                THẦY
              </span>
            )}
            <span
              className={
                compact
                  ? "min-w-0 flex-1 truncate text-sm font-semibold text-foreground"
                  : "ml-1 font-serif-soft text-base text-gold-gradient"
              }
            >
              {displayName}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-primary/60" />
          </button>

          <div className="mx-1 h-5 w-px bg-gold-soft/20" />

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger
                type="button"
                className="mr-1 flex h-8 w-8 cursor-help items-center justify-center rounded-full transition-colors hover:bg-primary/10 focus:outline-none"
              >
                <Info className="h-[15px] w-[15px] text-primary/60" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="end"
                className="bg-popover/95 backdrop-blur-md border-gold-soft/30 max-w-[200px] z-[110]"
              >
                <p className="text-[13px] text-popover-foreground text-center font-serif-soft leading-relaxed">
                  Mỗi vị minh sư có một phong cách luận giải riêng. Hãy chọn vị thầy phù hợp với cơ
                  duyên của bạn.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      <DivineRosterDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        currentId={value}
        onSelect={handleSelect}
        diviners={diviners}
        loading={loading}
        error={error}
      />
    </>
  );
}
